import type {
  TreeHierarchyCheck,
  TreeHierarchyContext,
  TreeHierarchyItem,
  TreeHierarchyWorkflow,
} from '../types'
import type { WorkflowListItem, CheckPayload } from '@app/shared/types/sf-data'
import { SilverfinContextCamelCase } from '@app/shared/types/sf-data'
import type TreeNode from '@app/utils/tree'

import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { getChecksByLedger } from '../api'
import { minimumSearchQueryLength } from '../constants'
import { ChecksCalculationStatus } from '../types'
import { generateChecksTreeHierarchy } from './utils'
import { SfLog } from '@app/adapters/logger'
import { getWorkflows } from '@app/application/communication/screens/inbox/api/workflows'
import useInboxUrlStorage from '@app/application/communication/screens/inbox/composables/use-inbox-url-storage'
import { useSfAssistantChecksReadStatus } from '@app/application/sf-assistant-checks/stores/assistant-checks-read-status'
import { useSfAssistantChecksCommentIds } from '@app/application/sf-assistant-checks/stores/assistant-checks-comment-ids'

export const useSfAssistantChecksPage = defineStore(
  'sfAssistantChecksPage',
  () => {
    const assistantChecksReadStatusStore = useSfAssistantChecksReadStatus()
    const {
      setReadStatusChecks,
      setReadByUsersChecks,
      markChecksAsRead,
      toggleCheck,
    } = assistantChecksReadStatusStore
    const { checkReadStatus, checkReadByUsers } = storeToRefs(
      assistantChecksReadStatusStore
    )

    const assistantChecksCommentIdsStore = useSfAssistantChecksCommentIds()
    const { setCommentIds } = assistantChecksCommentIdsStore

    const checksTree = ref<TreeNode<TreeHierarchyItem> | null>(null)
    const dataStatus = ref<ChecksCalculationStatus>(
      ChecksCalculationStatus.UpToDate
    )
    const loading = ref(false)
    const searchQuery = useInboxUrlStorage('searchTerm', '', (val) => val)

    const rawReadByFilter = useInboxUrlStorage<string>(
      'readByFilter',
      '',
      (val) => val ?? ''
    )

    const readByFilter = computed<string[]>({
      get() {
        // Split the stored string into an array
        return rawReadByFilter.value ? rawReadByFilter.value.split(',') : []
      },
      set(arr: string[]) {
        // Join array back into comma-separated string
        rawReadByFilter.value = arr.length ? arr.join(',') : ''
      },
    })

    const checkTypeFilter = useInboxUrlStorage<string>(
      'checkTypeFilter',
      'any',
      (val) => val
    )

    const receivedInitialSearchQuery = ref<boolean>(false)
    const workflowId = ref<number | null>(null)

    const checksById = computed((): Record<string, TreeHierarchyCheck> => {
      if (!checksTree.value) {
        return {}
      }

      return checksTree.value.reduce<Record<string, TreeHierarchyCheck>>(
        (acc, treeItem) => {
          if (treeItem.type === 'check') {
            acc[treeItem.id] = treeItem
          }

          return acc
        },
        {}
      )
    })

    const hasChecks = computed(() => {
      return Object.keys(checksById.value).length > 0
    })

    const contextById = computed((): Record<
      string,
      Omit<TreeHierarchyContext, 'children'>
    > => {
      if (!checksTree.value) {
        return {}
      }

      return checksTree.value.reduce<
        Record<string, Omit<TreeHierarchyContext, 'children'>>
      >((acc, treeItem) => {
        if (
          treeItem.type === SilverfinContextCamelCase.Account ||
          treeItem.type === SilverfinContextCamelCase.ReconciliationText
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { children, ...contextItem } = treeItem
          acc[treeItem.id] = contextItem
        }

        return acc
      }, {})
    })

    const workflowByIdTree = computed((): Record<
      string,
      Omit<TreeHierarchyWorkflow, 'children'>
    > => {
      if (!checksTree.value) {
        return {}
      }

      return checksTree.value.reduce<
        Record<string, Omit<TreeHierarchyWorkflow, 'children'>>
      >((acc, treeItem) => {
        if (treeItem.type === 'workflow') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { children, ...workflowItem } = treeItem
          acc[treeItem.id] = workflowItem
        }

        return acc
      }, {})
    })

    const isSearchQueryPresent = computed(() => {
      return (
        searchQuery.value &&
        searchQuery.value.length >= minimumSearchQueryLength
      )
    })

    const isFiltered = computed(() => {
      return (
        isSearchQueryPresent.value ||
        readByFilter.value.length > 0 ||
        checkTypeFilter.value !== 'any'
      )
    })

    const filteredHierarchyTree = computed(() => {
      if (!checksTree.value) {
        return checksTree.value
      }

      if (!isFiltered.value) {
        return checksTree.value.map((treeItem) => ({
          id: treeItem.id,
          type: treeItem.type,
        }))
      }

      const userFilterActive = readByFilter.value.length > 0
      const nobodySelected = readByFilter.value.includes('nobody')
      const mentionsFilterActive = checkTypeFilter.value === 'mentions'

      const filteredTree = checksTree.value.filter((treeItem) => {
        if (!treeItem) return false

        if (treeItem.type !== 'check') return true

        const descriptionMatches = (
          treeItem?.text?.[window.I18n.locale]?.description ?? ''
        )
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase())

        let matchesReadByFilter = true

        if (userFilterActive) {
          const onlyNobodySelected =
            nobodySelected && readByFilter.value.length === 1

          if (onlyNobodySelected) {
            matchesReadByFilter =
              (!checkReadByUsers.value[treeItem.id] ||
                checkReadByUsers.value[treeItem.id].length === 0) &&
              !checkReadStatus.value[treeItem.id]
          } else {
            const meMatches =
              userFilterActive &&
              readByFilter.value.includes('me') &&
              checkReadStatus.value[treeItem.id] === true

            const userMatches = (
              checkReadByUsers.value[treeItem.id] || []
            ).some((userName) =>
              readByFilter.value.includes(userName.toLowerCase())
            )

            const nobodyMatches =
              nobodySelected &&
              (!checkReadByUsers.value[treeItem.id] ||
                checkReadByUsers.value[treeItem.id].length === 0) &&
              !checkReadStatus.value[treeItem.id]

            matchesReadByFilter = meMatches || userMatches || nobodyMatches
          }
        }

        let matchesMentionsFilter = true

        if (mentionsFilterActive) {
          matchesMentionsFilter =
            treeItem.type === 'check' && treeItem.currentUserMentioned === true
        }

        return (
          descriptionMatches && matchesReadByFilter && matchesMentionsFilter
        )
      })

      if (filteredTree.isEmpty()) return filteredTree

      return (filteredTree as TreeNode<TreeHierarchyItem>).map((treeItem) => ({
        id: treeItem.id,
        type: treeItem.type,
      }))
    })

    const isFilteredListEmpty = computed(() => {
      if (filteredHierarchyTree.value === null) {
        return false
      }

      let filteredTreeHasChecks = false
      for (const item of filteredHierarchyTree.value) {
        filteredTreeHasChecks ||= item.unwrap()?.type === 'check'
      }

      return !filteredTreeHasChecks && hasChecks.value && isFiltered.value
    })

    const filteredHierarchyArray = computed(() => {
      const hierarchyArray = filteredHierarchyTree.value?.unwrapDeep(true)

      if (
        !hierarchyArray ||
        hierarchyArray.length === 0 ||
        !hierarchyArray[0] ||
        (hierarchyArray.length === 1 && !hierarchyArray[0]) ||
        'children' in hierarchyArray[0] === false
      ) {
        return []
      }

      return hierarchyArray[0].children?.filter(
        (child) => child.id !== 'general' && child.children
      )
    })

    const hasNoChecksDueToSearch = computed(() => {
      if (filteredHierarchyTree.value === null) {
        return false
      }
      let filteredTreeHasChecks = false
      for (const item of filteredHierarchyTree.value) {
        filteredTreeHasChecks ||= item.unwrap()?.type === 'check'
      }
      return (
        !filteredTreeHasChecks &&
        hasChecks.value &&
        searchQuery.value.length >= minimumSearchQueryLength
      )
    })

    const generalCheckIds = computed(() => {
      if (!filteredHierarchyTree.value) {
        return []
      }

      const generalWorkflowBranch = filteredHierarchyTree.value.find(
        (treeItem) => treeItem?.id === 'general'
      )

      if (generalWorkflowBranch.isEmpty()) {
        return []
      }

      return (generalWorkflowBranch as TreeNode<TreeHierarchyWorkflow>)
        .map((treeItem) => treeItem.id)
        .unwrapDeep(false)
    })

    const generalUnreadCheckIds = computed(() => {
      if (!generalCheckIds.value) {
        return []
      }

      return generalCheckIds.value.filter((checkId) => {
        return !checkReadStatus.value[checkId]
      })
    })

    const groupedCheckIds = computed(() => {
      if (!filteredHierarchyTree.value) {
        return {}
      }

      return filteredHierarchyTree.value
        .unwrapDeep(false)
        .reduce((workflowAccumulator, workflowItem) => {
          if (
            !workflowItem ||
            workflowItem.type !== 'workflow' ||
            'children' in workflowItem === false
          ) {
            return workflowAccumulator
          }

          workflowAccumulator[workflowItem.id] = workflowItem.children.reduce(
            (contextAccumulator, contextItem) => {
              if (
                !contextItem ||
                'children' in contextItem === false ||
                (contextItem.type !== SilverfinContextCamelCase.Account &&
                  contextItem.type !==
                    SilverfinContextCamelCase.ReconciliationText)
              ) {
                return contextAccumulator
              }

              contextAccumulator[contextItem.id] = contextItem.children.reduce(
                (checkAccumulator, checkItem) => {
                  checkAccumulator.push(checkItem.id)
                  return checkAccumulator
                },
                [] as string[]
              )

              return contextAccumulator
            },
            {} as Record<string, string[]>
          )

          return workflowAccumulator
        }, {} as Record<string, Record<string, string[]>>)
    })

    const unreadHierarchyTree = computed(() => {
      if (!filteredHierarchyTree.value) {
        return null
      }

      return filteredHierarchyTree.value.filter((treeItem) => {
        if (!treeItem?.id) {
          return false
        }

        if (treeItem.type === 'check') {
          return !checkReadStatus.value[treeItem.id]
        }

        return true
      })
    })

    const unreadCheckIds = computed(() => {
      if (!unreadHierarchyTree.value) {
        return []
      }

      return unreadHierarchyTree.value.reduce((acc, treeItem) => {
        if (!treeItem) {
          return acc
        }

        if (
          treeItem?.type === 'check' &&
          treeItem.id &&
          !acc.includes(treeItem.id)
        ) {
          acc.push(treeItem.id)
        }

        return acc
      }, [] as string[])
    })

    const unreadHierarchyByIdCount = computed(() => {
      const unreadByType: Record<
        'contexts' | 'workflows',
        Record<string, number>
      > = {
        contexts: {},
        workflows: {},
      }

      if (!unreadHierarchyTree.value) {
        return unreadByType
      }

      // We reverse traverse the tree to make sure that by the time workflows are checked, the relevant context unread count is already calculated
      for (const treeItem of unreadHierarchyTree.value.reverseIterator()) {
        const item = treeItem.unwrap()

        if (!item) {
          continue
        }

        const hasNestedChecks =
          (item.type === 'workflow' && item.id === 'general') ||
          item.type === SilverfinContextCamelCase.Account ||
          item.type === SilverfinContextCamelCase.ReconciliationText

        const hasNestedContexts =
          item.type === 'workflow' && item.id !== 'general'

        if (!hasNestedChecks && !hasNestedContexts) {
          continue
        }

        if (hasNestedChecks) {
          const children = treeItem.unwrapDeep(false) as TreeHierarchyCheck[]
          const count = children.reduce((unreadCount, check) => {
            if (!checkReadStatus.value[check.id]) {
              return unreadCount + 1
            }

            return unreadCount
          }, 0)

          if (item.type === 'workflow') {
            unreadByType.workflows[item.id] = count
          } else {
            unreadByType.contexts[item.id] = count
          }
        } else if (hasNestedContexts) {
          const children = treeItem.unwrapDeep(false) as TreeHierarchyContext[]
          const count = children.reduce((unreadCount, context) => {
            return unreadCount + unreadByType.contexts[context.id]
          }, 0)
          unreadByType.workflows[item.id] = count
        }
      }

      return unreadByType
    })

    const filteredUnreadCheckCount = computed(() => {
      return unreadCheckIds.value.length
    })

    const workflowIdsTree = computed(() => {
      if (!filteredHierarchyTree.value) {
        return []
      }

      return filteredHierarchyTree.value.reduce((acc, treeItem) => {
        if (!treeItem) {
          return acc
        }

        // Exclude the mock general workflow
        if (treeItem.type === 'workflow' && treeItem.id !== 'general') {
          acc.push(treeItem.id)
        }

        return acc
      }, [] as Array<string | number>)
    })

    const triggerCalculation = {
      channel: '',
      lastId: 0,
      stale: false,
    }

    const fetchAll = async (): Promise<void> => {
      loading.value = true

      try {
        const [workflows, checks] = await Promise.all([
          receiveWorkflows(),
          receiveChecks(),
        ])

        if (checks !== null) {
          triggerCalculation.channel = checks.channel
          triggerCalculation.lastId = checks.last_id
          triggerCalculation.stale = checks.stale
        }

        if (workflows !== null && checks !== null) {
          setReadStatusChecks(checks.readStatus)
          setReadByUsersChecks(checks.readByUsers)

          checksTree.value = generateChecksTreeHierarchy({
            checksResponse: checks,
            workflowId: workflowId.value,
            workflows,
          })

          setCommentIds(Object.values(checksById.value))

          if (checks.applyMentionsFilter) {
            checkTypeFilter.value = 'mentions'
          }
        }
      } catch (error) {
        SfLog.warn('Failed to load workflows/checks', {
          extra: {
            ...window.Silverfin.jsonParams,
            error,
          },
        })
      } finally {
        loading.value = false
      }
    }

    const receiveInitialSearchQuery = (value: string): void => {
      searchQuery.value = value
      setTimeout(() => {
        receivedInitialSearchQuery.value = true
      })
    }
    const clearSearchQuery = (): void => {
      searchQuery.value = ''
    }

    const clearFilters = (): void => {
      readByFilter.value = []
      checkTypeFilter.value = 'any'
      searchQuery.value = ''
    }

    const receiveChecks = async (): Promise<CheckPayload | null> => {
      try {
        const options = {
          skip_platform_checks: true,
          ...(workflowId.value ? { workflow_id: workflowId.value } : {}),
        }

        return await getChecksByLedger(options)
      } catch (error) {
        SfLog.warn('Page -> receiveChecks failed', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
        throw error
      }
    }

    const receiveWorkflows = async (): Promise<WorkflowListItem[] | null> => {
      try {
        return await getWorkflows({
          ledgerId: window.Silverfin.currentLedgerId(),
          company_id: window.Silverfin.currentCompanyId,
        })
      } catch (error) {
        SfLog.warn('Failed to load company workflows', {
          extra: {
            ...window.Silverfin.jsonParams,
            error,
          },
        })
        throw error
      }
    }

    const searchFor = (query: string): void => {
      searchQuery.value = query
    }

    const setWorkflowId = (context: number): void => {
      workflowId.value = context
    }

    const noFilterOrSearchResults = computed(() => {
      return (
        (isFilteredListEmpty.value && hasNoChecksDueToSearch.value) ||
        (isFilteredListEmpty.value &&
          (searchQuery.value === '' || searchQuery.value.length < 3))
      )
    })

    return {
      checkReadStatus,
      checkReadByUsers,
      dataStatus,
      hierarchy: filteredHierarchyArray,
      hasChecks,
      hasNoChecksDueToSearch,
      unreadCheckIds,
      unreadHierarchyTree,
      filteredUnreadCheckCount,
      generalUnreadCheckIds,
      groupedCheckIds,
      contextById,
      workflowIds: workflowIdsTree,
      workflowById: workflowByIdTree,
      checksById,
      generalCheckIds,
      unreadHierarchyByIdCount,
      loading,
      searchQuery,
      isSearchQueryPresent,
      receivedInitialSearchQuery,
      receiveInitialSearchQuery,
      clearSearchQuery,
      readByFilter,
      isFiltered,
      isFilteredListEmpty,
      clearFilters,
      checkTypeFilter,
      noFilterOrSearchResults,
      triggerCalculation,
      fetchAll,
      toggleCheck,
      markChecksAsRead,
      searchFor,
      setWorkflowId,
    }
  }
)
